import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getBusinessById, createBusiness, updateBusiness, getAllCategories, getLocations } from '../../lib/api'
import { businessSchema } from '../../lib/schemas'
import { Input } from '../../components/ui/Input'
import { Textarea } from '../../components/ui/Textarea'
import { Select } from '../../components/ui/Select'
import { Toggle } from '../../components/ui/Toggle'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import { slugify } from '../../lib/utils'

export default function BusinessForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: categories } = useQuery({ queryKey: ['categories'], queryFn: getAllCategories })
  const { data: locations } = useQuery({ queryKey: ['locations'], queryFn: getLocations })
  const { data: business, isLoading } = useQuery({
    queryKey: ['business', id],
    queryFn: () => getBusinessById(id),
    enabled: isEdit,
  })

  const { register, handleSubmit, control, setValue, reset, watch, formState: { errors } } = useForm({
    resolver: zodResolver(businessSchema),
    defaultValues: { featured: false, sponsored: false, ourPick: false, isActive: true },
  })

  const nameValue = watch('name')
  useEffect(() => {
    if (!isEdit && nameValue) setValue('slug', slugify(nameValue))
  }, [nameValue, isEdit, setValue])

  useEffect(() => {
    if (business) {
      reset({
        name:        business.name        ?? '',
        slug:        business.slug        ?? '',
        categoryId:  business.categoryId  ?? '',
        country:     business.country     ?? '',
        city:        business.city        ?? '',
        address:     business.address     ?? '',
        phone:       business.phone       ?? '',
        website:     business.website     ?? '',
        email:       business.email       ?? '',
        instagram:   business.instagram   ?? '',
        hours:       business.hours       ?? '',
        description: business.description ?? '',
        mapLink:     business.mapLink     ?? '',
        featured:    Boolean(business.featured),
        sponsored:   Boolean(business.sponsored),
        ourPick:     Boolean(business.ourPick),
        onlineOnly:  Boolean(business.onlineOnly),
        isActive:    Boolean(business.isActive),
      })
    }
  }, [business, reset])

  function toSlug(str) {
    return (str ?? '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[İ]/g, 'i').replace(/[ı]/g, 'i')
      .replace(/[ğĞ]/g, 'g').replace(/[şŞ]/g, 's').replace(/[çÇ]/g, 'c')
      .toLowerCase().trim()
      .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-')
  }

  function handleLocationChange(e) {
    const loc = locations?.find(l => `${l.country}::${l.city}` === e.target.value)
    if (loc) {
      // Store URL-safe slugs so frontend routes like /afghanistan/kabul work correctly
      setValue('country', toSlug(loc.countryLabel))
      setValue('city', toSlug(loc.cityLabel))
    }
  }

  const onlineOnly = watch('onlineOnly')
  const currentCountry = watch('country')
  const currentCity = watch('city')
  const matchedLocation = locations?.find(
    l => toSlug(l.countryLabel) === currentCountry && toSlug(l.cityLabel) === currentCity
  )
  const locationSelectValue = matchedLocation ? `${matchedLocation.country}::${matchedLocation.city}` : ''

  const mutation = useMutation({
    mutationFn: (data) => isEdit ? updateBusiness(id, data) : createBusiness(data),
    onSuccess: () => {
      qc.invalidateQueries(['businesses'])
      qc.invalidateQueries(['stats'])
      toast.success(isEdit ? 'Business updated' : 'Business created')
      navigate('/businesses')
    },
    onError: (e) => toast.error(e.message),
  })

  if (isEdit && isLoading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>

  return (
    <div>
      <div className="flex items-center gap-3 mb-8">
        <button type="button" onClick={() => navigate('/businesses')} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
          <i className="ti ti-arrow-left" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Business' : 'New Business'}</h1>
          <p className="text-sm text-gray-500 mt-0.5">Business listing details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-4">

        {/* Basic Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">Basic Info</h2>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Business Name" placeholder="e.g. Kabul Restaurant" error={errors.name?.message} {...register('name')} />
            <Input label="Slug (URL)" placeholder="kabul-restaurant" error={errors.slug?.message} {...register('slug')} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Select label="Category" error={errors.categoryId?.message} {...register('categoryId')}>
              <option value="">Select category...</option>
              {categories?.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </Select>
            <Input label="Phone" optional placeholder="+49 555 123 4567" hint="Used for Call & WhatsApp buttons" error={errors.phone?.message} {...register('phone')} />
          </div>

          {/* Location select — hidden when Online Only */}
          {!onlineOnly ? (
            <>
              <Select
                label="Location"
                hint="Selecting auto-fills country & city"
                value={locationSelectValue}
                onChange={handleLocationChange}
              >
                <option value="">Select a location...</option>
                {locations?.map(l => (
                  <option key={`${l.country}::${l.city}`} value={`${l.country}::${l.city}`}>
                    {l.flag && !l.flag.startsWith('http') ? `${l.flag} ` : ''}{l.cityLabel}, {l.countryLabel}
                  </option>
                ))}
              </Select>
              <input type="hidden" {...register('country')} />
              <input type="hidden" {...register('city')} />
            </>
          ) : (
            <div className="rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-xs text-sky-700 flex items-center gap-2">
              <i className="ti ti-world shrink-0" />
              <span>Online Only — no location required.</span>
            </div>
          )}

          <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-xs text-blue-700 flex items-start gap-2">
            <i className="ti ti-info-circle mt-0.5 shrink-0" />
            <span>
              Address is optional. If this business only has Instagram or WhatsApp, you can leave it blank — the website will show a "contact us to add address" notice.
            </span>
          </div>
          <Input
            label="Full Address"
            optional
            placeholder="123 Main St, City"
            error={errors.address?.message}
            {...register('address')}
          />
          <Input
            label="Google Maps Link"
            optional
            type="url"
            placeholder="https://maps.google.com/..."
            hint="Paste the Google Maps share link"
            error={errors.mapLink?.message}
            {...register('mapLink')}
          />
        </div>

        {/* Contact & Social */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">Contact & Social</h2>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Website" optional type="url" placeholder="https://example.com" error={errors.website?.message} {...register('website')} />
            <Input label="Email" optional type="email" placeholder="info@example.com" error={errors.email?.message} {...register('email')} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Instagram" optional placeholder="https://instagram.com/handle" error={errors.instagram?.message} {...register('instagram')} />
            <Input label="Working Hours" optional placeholder="Mon-Fri 09:00-18:00" error={errors.hours?.message} {...register('hours')} />
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">Description</h2>
          <Textarea label="Description" placeholder="Write a short description about this business..." error={errors.description?.message} rows={5} {...register('description')} />
        </div>

        {/* Settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Settings</h2>
          <div className="grid grid-cols-2 gap-4">
            <Controller name="featured" control={control} render={({ field }) => (
              <Toggle label="Featured" checked={field.value} onChange={field.onChange} />
            )} />
            <Controller name="sponsored" control={control} render={({ field }) => (
              <Toggle label="Sponsored" checked={field.value} onChange={field.onChange} />
            )} />
            <Controller name="ourPick" control={control} render={({ field }) => (
              <Toggle label="Our Pick" checked={field.value} onChange={field.onChange} />
            )} />
            <Controller name="onlineOnly" control={control} render={({ field }) => (
              <Toggle
                label="Online Only"
                description="No physical address — Instagram/WhatsApp only"
                checked={field.value}
                onChange={field.onChange}
              />
            )} />
            <Controller name="isActive" control={control} render={({ field }) => (
              <Toggle label="Active" checked={field.value} onChange={field.onChange} />
            )} />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
          <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
            {isEdit ? 'Update Business' : 'Create Business'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/businesses')}>Cancel</Button>
        </div>
      </form>
    </div>
  )
}
